/**
 * The selected agent's active prompt (task 796).
 *
 * Two actions (task 1310 relabelled them; the heading already says "prompt"):
 *
 *   - **Edit** links to `/prompts/:id`, the existing prompt page.
 *   - **Change** opens `AgentPromptSelector`, the modal the Advanced agent page
 *     already uses, in place.
 *
 * The standalone History action is gone (task 1310): the version chip beside
 * the prompt's name already opens `VersionHistoryPanel`, and the Agents page
 * gives the prompt's history its own sub tab. `full` drops the 300px clamp so
 * the Prompt tab can show the prompt whole.
 *
 * Report open question 3 — does "Change prompt" in place count as editing, and
 * should Phase 1 restrict this tab to links-out only? Current implementation:
 * permitted. It mounts the existing selector modal, which carries the existing
 * assignment mutation and therefore the existing permission behaviour; no new
 * edit path and no new permission rule is introduced by rendering it here. If a
 * later role decision says the service-scoped view must be strictly read-only,
 * this is the single button to remove.
 *
 * The version chip and History follow the account's adoption of Publishing &
 * versions: `useResourceVersionStatus` answers `live-editing` without a network
 * call while the practice is dormant, and this panel renders neither affordance
 * in that state — the same rule `ResourceVersionControls` applies everywhere
 * else.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useActiveAgentPrompt } from '@/hooks/useAgents';
import { useFlag } from '@/hooks/useFeatureFlags';
import { useResourceVersionStatus } from '@/hooks/useResourceVersions';
import { AgentPromptSelector } from '@/components/agents/AgentPromptSelector';
import { VersionHistoryPanel } from '@/components/versioning/VersionHistoryPanel';
import { VersionStatusChip } from '@/components/versioning/VersionStatusChip';

interface AgentPromptPanelProps {
  agentId: string;
  /** Show the whole prompt, without the 300px clamp (the Agents page's Prompt tab, task 1310). */
  full?: boolean;
  /**
   * Fill the card (the Agents page's Overview, task 1312): the panel stretches
   * to its grid row and the prompt scrolls inside the space left, so the prompt
   * fills the card instead of setting the row's height.
   */
  fill?: boolean;
}

const actionClasses =
  'text-xs font-semibold text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300';

export function AgentPromptPanel({ agentId, full = false, fill = false }: AgentPromptPanelProps) {
  const { data: prompt, isLoading, isError } = useActiveAgentPrompt(agentId);
  const versioningFlag = useFlag('innovate.fe.resource-versioning');
  const { status } = useResourceVersionStatus('prompt', prompt?.id);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const versioningAdopted = versioningFlag && status.versioningEnabled;

  return (
    <section
      data-testid="agent-prompt-panel"
      className={`${fill ? 'flex h-full flex-col ' : ''}rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Active prompt</h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {prompt ? (
              <span className="rounded border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200">
                {prompt.name}
              </span>
            ) : (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {isLoading ? 'Loading…' : 'No prompt assigned'}
              </span>
            )}
            {versioningAdopted && prompt && (
              <VersionStatusChip status={status} onOpenHistory={() => setHistoryOpen(true)} />
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-4">
          {prompt && (
            <Link to={`/prompts/${prompt.id}`} data-testid="agent-edit-prompt" className={actionClasses}>
              Edit
            </Link>
          )}
          <button
            type="button"
            onClick={() => setSelectorOpen(true)}
            data-testid="agent-change-prompt"
            className={actionClasses}
          >
            Change
          </button>
        </div>
      </div>

      {isError ? (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400">
          The active prompt could not be loaded.
        </p>
      ) : prompt && fill ? (
        <div className="relative mt-3 min-h-[300px] flex-1">
          <pre
            data-testid="agent-prompt-content"
            className="absolute inset-0 overflow-y-auto whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-xs leading-relaxed text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
          >
            {prompt.content}
          </pre>
        </div>
      ) : prompt ? (
        <pre
          data-testid="agent-prompt-content"
          className={`mt-3 ${full ? '' : 'max-h-[300px] overflow-y-auto '}whitespace-pre-wrap rounded-lg border border-gray-200 bg-gray-50 p-3 font-mono text-xs leading-relaxed text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300`}
        >
          {prompt.content}
        </pre>
      ) : (
        !isLoading && (
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            No active prompt is assigned to this agent, so it runs on its description alone.
          </p>
        )
      )}

      <AgentPromptSelector
        agentId={agentId}
        isOpen={selectorOpen}
        onClose={() => setSelectorOpen(false)}
      />

      {versioningAdopted && prompt && (
        <VersionHistoryPanel
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          entityType="prompt"
          entityId={prompt.id}
          resourceName={prompt.name}
        />
      )}
    </section>
  );
}

export default AgentPromptPanel;
